import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, Bus, Search, X, CheckCircle, AlertCircle, UserPlus } from "lucide-react";
import { getApiUrl } from "../../config/api";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface TransportRoute {
  id: number;
  name: string;
  description?: string | null;
  driverName?: string | null;
  driverContact?: string | null;
  fare?: number | null;
}

interface Learner {
  id: number;
  name: string;
  email?: string;
}

const TransportRoutes: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const [routes, setRoutes] = useState<TransportRoute[]>([]);
  const [learners, setLearners] = useState<Learner[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  useAutoDismiss(message, setMessage, null, 5000);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState<TransportRoute | null>(null);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [assignLearnerId, setAssignLearnerId] = useState("");
  const [assignRouteId, setAssignRouteId] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    driverName: "",
    driverContact: "",
    fare: ""
  });

  const fetchRoutes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl("transport/routes"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoutes(res.data?.routes || []);
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.message || "Failed to load transport routes" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes();
    fetchLearners();
  }, []);

  const fetchLearners = async () => {
    try {
      const res = await axios.get(getApiUrl("users/students"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLearners(res.data?.students || []);
    } catch {
      setLearners([]);
    }
  };

  const filteredRoutes = useMemo(() => {
    if (!searchTerm.trim()) return routes;
    const q = searchTerm.toLowerCase();
    return routes.filter((r) =>
      r.name.toLowerCase().includes(q) ||
      (r.driverName || "").toLowerCase().includes(q) ||
      (r.driverContact || "").toLowerCase().includes(q)
    );
  }, [routes, searchTerm]);

  const openAddModal = () => {
    setEditingRoute(null);
    setFormData({ name: "", description: "", driverName: "", driverContact: "", fare: "" });
    setIsModalOpen(true);
  };

  const openEditModal = (route: TransportRoute) => {
    setEditingRoute(route);
    setFormData({
      name: route.name || "",
      description: route.description || "",
      driverName: route.driverName || "",
      driverContact: route.driverContact || "",
      fare: route.fare !== null && route.fare !== undefined ? String(route.fare) : ""
    });
    setIsModalOpen(true);
  };

  const openAssignModal = () => {
    setAssignLearnerId("");
    setAssignRouteId("");
    setIsAssignOpen(true);
  };

  const handleAssignRoute = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assignLearnerId) {
      setMessage({ type: "error", text: "Select a learner to assign a route." });
      return;
    }
    try {
      await axios.patch(
        getApiUrl(`users/${assignLearnerId}/transport`),
        {
          transportRouteId: assignRouteId || null,
          transportMode: assignRouteId ? "school" : "personal"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: "success", text: "Route assigned successfully." });
      setIsAssignOpen(false);
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.message || "Failed to assign route." });
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setMessage({ type: "error", text: "Route name is required" });
      return;
    }
    try {
      if (editingRoute) {
        await axios.put(
          getApiUrl(`transport/routes/${editingRoute.id}`),
          {
            ...formData,
            fare: Number(formData.fare || 0)
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage({ type: "success", text: "Transport route updated" });
      } else {
        await axios.post(
          getApiUrl("transport/routes"),
          {
            ...formData,
            fare: Number(formData.fare || 0)
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage({ type: "success", text: "Transport route created" });
      }
      setIsModalOpen(false);
      fetchRoutes();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.message || "Failed to save route" });
    }
  };

  const handleDelete = async (routeId: number) => {
    if (!window.confirm("Delete this transport route?")) return;
    try {
      await axios.delete(getApiUrl(`transport/routes/${routeId}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: "success", text: "Transport route deleted" });
      fetchRoutes();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.message || "Failed to delete route" });
    }
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main p-8 bg-slate-50/30">
        <div className="utopia-page-container">
          <div className="utopia-table-header">
            <div>
              <h2 className="text-2xl font-extrabold text-slate-900">Transport Routes</h2>
              <p className="page-subtitle text-slate-500">Manage transport routes, drivers, and fares.</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative w-72">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className="utopia-input pl-9 border-teal-100 focus:border-teal-500 focus:ring-teal-500/20"
                  placeholder="Search routes, drivers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button className="utopia-btn-primary bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 flex items-center gap-2 whitespace-nowrap" onClick={openAddModal}>
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20 text-white text-lg leading-none">+</span>
                <span>Add Route</span>
              </button>
              <button className="utopia-btn-secondary px-6 py-3 flex items-center gap-2 whitespace-nowrap" onClick={openAssignModal}>
                <UserPlus size={16} />
                <span>Assign Route</span>
              </button>
            </div>
          </div>

          {message && (
            <div className={`mb-4 rounded-lg border px-4 py-3 text-sm ${message.type === "success" ? "border-emerald-100 bg-emerald-50 text-emerald-700" : "border-rose-100 bg-rose-50 text-rose-700"}`}>
              <div className="flex items-center gap-2">
                {message.type === "success" ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                {message.text}
              </div>
            </div>
          )}

          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading transport routes...</div>
          ) : (
            <table className="utopia-table text-[15px]">
              <thead>
                <tr className="bg-slate-50 text-slate-600 uppercase text-xs tracking-widest">
                  <th className="py-4">Route Name</th>
                  <th className="py-4">Route Description</th>
                  <th className="py-4">Driver Name</th>
                  <th className="py-4">Driver Contact</th>
                  <th className="py-4">Fare</th>
                  <th className="text-right py-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoutes.map((route) => (
                  <tr key={route.id} className="hover:bg-teal-50/40 transition-colors">
                    <td className="font-bold text-slate-800">{route.name}</td>
                    <td className="text-slate-600">{route.description || "-"}</td>
                    <td className="text-slate-700 font-semibold">{route.driverName || "-"}</td>
                    <td className="text-slate-600">{route.driverContact || "-"}</td>
                    <td className="text-slate-700 font-semibold">{route.fare ? `K ${Number(route.fare).toFixed(2)}` : "-"}</td>
                    <td className="text-right">
                      <div className="flex justify-end gap-2">
                        <button className="utopia-btn-icon text-teal-600 hover:text-teal-700" onClick={() => openEditModal(route)}>
                          <Edit2 size={16} />
                        </button>
                        <button className="utopia-btn-icon text-rose-600 hover:text-rose-700" onClick={() => handleDelete(route.id)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredRoutes.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-slate-400 py-8">
                      No transport routes found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {isModalOpen && (
        <div className="utopia-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsModalOpen(false)}>
          <div className="utopia-modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header">
              <div className="flex items-center gap-2">
                <Bus size={18} />
                <h3 className="text-lg font-bold">{editingRoute ? "Edit Route" : "Add New Route"}</h3>
              </div>
              <button className="utopia-modal-close-btn" onClick={() => setIsModalOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="utopia-form-group">
                <label className="utopia-label required">Route Name</label>
                <input
                  className="utopia-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Coronation Park"
                  required
                />
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label">Route Description</label>
                <input
                  className="utopia-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Transport"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="utopia-form-group">
                  <label className="utopia-label">Driver Name</label>
                  <input
                    className="utopia-input"
                    value={formData.driverName}
                    onChange={(e) => setFormData({ ...formData, driverName: e.target.value })}
                    placeholder="Driver name"
                  />
                </div>
                <div className="utopia-form-group">
                  <label className="utopia-label">Driver Contact</label>
                  <input
                    className="utopia-input"
                    value={formData.driverContact}
                    onChange={(e) => setFormData({ ...formData, driverContact: e.target.value })}
                    placeholder="Phone number"
                  />
                </div>
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label">Fare (ZMW)</label>
                <input
                  type="number"
                  className="utopia-input"
                  value={formData.fare}
                  onChange={(e) => setFormData({ ...formData, fare: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="flex gap-3 mt-4">
                <button type="button" className="utopia-btn-secondary flex-1" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="utopia-btn-primary flex-1">
                  {editingRoute ? "Update Route" : "Create Route"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isAssignOpen && (
        <div className="utopia-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsAssignOpen(false)}>
          <div className="utopia-modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header">
              <div className="flex items-center gap-2">
                <UserPlus size={18} />
                <h3 className="text-lg font-bold">Assign Route to Learner</h3>
              </div>
              <button className="utopia-modal-close-btn" onClick={() => setIsAssignOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleAssignRoute} className="space-y-4">
              <div className="utopia-form-group">
                <label className="utopia-label required">Learner</label>
                <select
                  className="utopia-input"
                  value={assignLearnerId}
                  onChange={(e) => setAssignLearnerId(e.target.value)}
                  required
                >
                  <option value="">Select learner...</option>
                  {learners.map((learner) => (
                    <option key={learner.id} value={learner.id}>
                      {learner.name} {learner.email ? `• ${learner.email}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label">Route</label>
                <select
                  className="utopia-input"
                  value={assignRouteId}
                  onChange={(e) => setAssignRouteId(e.target.value)}
                >
                  <option value="">Personal (No school transport)</option>
                  {routes.map((route) => (
                    <option key={route.id} value={route.id}>
                      {route.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" className="utopia-btn-secondary" onClick={() => setIsAssignOpen(false)}>Cancel</button>
                <button type="submit" className="utopia-btn-primary">Assign</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportRoutes;
